import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle, CreditCard, Smartphone } from 'lucide-react';
import InterestCalculator from '../../components/InterestCalculator/Intrestcalculator';
import '../Home.css';
import '../../css/PanMobile.css';
import { squidloansAuthAPI, squidloansUserAPI } from '../../services/api';
import { useAuth } from '../../Context/AuthContext';
import { leadStageToRouteMap } from '../../Utils/constants';
import FlowFooter from '../../components/FlowFooter';
import landingBanner from '../../images/landingBanner.png';

function PanMobile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({ pan: '', phone: '' });
  const [errors, setErrors] = useState({ pan: '', phone: '' });
  const [agreed, setAgreed] = useState(false);
  const [notPoliticallyExposed, setNotPoliticallyExposed] = useState(false);
  const [showConsentError, setShowConsentError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [verifiedPhone, setVerifiedPhone] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [nextEventName, setNextEventName] = useState(null);
  const [isContinuingJourney, setIsContinuingJourney] = useState(false);

  // In-app browser redirect (same logic as reference PanMobile)
  function isInAppBrowser() {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    return /Instagram/i.test(ua) || /FBAN|FBAV|FB_IAB/i.test(ua);
  }

  function redirectToExternal() {
    const currentUrl = window.location.href;
    const ua = navigator.userAgent || '';
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    try {
      if (isAndroid && isInAppBrowser()) {
        const scheme = window.location.protocol.replace(':', '');
        const intentUrl = `intent://${currentUrl.replace(/^https?:\/\//, '')}#Intent;scheme=${scheme};package=com.android.chrome;end`;
        window.location.href = intentUrl;
      } else if (isIOS && isInAppBrowser()) {
        window.open(currentUrl, '_blank');
      }
    } catch (err) {
      console.error('Redirect failed:', err);
    }
  }

  useEffect(() => {
    if (isInAppBrowser()) {
      setTimeout(() => redirectToExternal(), 800);
      setTimeout(() => {
        alert(
          "Please open this page in your browser for a better experience. Tap the three dots (⋯) → 'Open in Browser'."
        );
      }, 2500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fill from URL params and store UTM
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const panParam = urlParams.get('pan');
    const mobileParam = urlParams.get('mobile');

    if (panParam || mobileParam) {
      setFormData((prev) => ({
        ...prev,
        pan: panParam ? panParam.toUpperCase() : prev.pan,
        phone: mobileParam || prev.phone,
      }));
    }

    const clickid = urlParams.get('clickid') || urlParams.get('click_id');
    const utmParams = {
      utm_source: urlParams.get('utm_source'),
      utm_medium: urlParams.get('utm_medium'),
      utm_term: urlParams.get('utm_term'),
      utm_content: urlParams.get('utm_content'),
      utm_campaign: urlParams.get('utm_campaign'),
      clickid: clickid || '',
    };
    const hasUtmParams = Object.values(utmParams).some((v) => v != null && v !== '');
    if (hasUtmParams) {
      sessionStorage.setItem('utmParams', JSON.stringify(utmParams));
    }
  }, [location.search]);

  // Auth check for "Continue Journey" (same logic as reference: getJourney + leadStageToRouteMap)
  useEffect(() => {
    const checkLoginStatus = async () => {
      setCheckingAuth(true);
      const token =
        localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('authtoken');
      const storedPhone = localStorage.getItem('phone_number') || localStorage.getItem('userPhone') || '';

      if (token && isAuthenticated) {
        try {
          const journey = await squidloansUserAPI.getJourney();
          const stage = journey?.journey?.lead_stage;
          if (stage) setNextEventName(stage);
          if (storedPhone) setVerifiedPhone(storedPhone);
        } catch (error) {
          // Handle 403/401 errors silently - don't show error to user
          // Just use stored phone if available
          if (error.response?.status === 403 || error.response?.status === 401) {
            console.warn('Journey fetch failed - token may be invalid');
          }
          if (storedPhone) setVerifiedPhone(storedPhone);
        }
      } else if (storedPhone && isAuthenticated) {
        setVerifiedPhone(storedPhone);
      }
      setCheckingAuth(false);
    };
    checkLoginStatus();
  }, [isAuthenticated]);

  const validatePAN = (pan) => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan);
  const validatePhone = (phone) => /^[6-9]\d{9}$/.test(phone);

  const getUTMParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const clickidFromUrl = urlParams.get('clickid') || urlParams.get('click_id');
    const stored = sessionStorage.getItem('utmParams');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return {
          utm_source: parsed.utm_source || '',
          utm_medium: parsed.utm_medium || '',
          utm_campaign: parsed.utm_campaign || '',
          utm_content: parsed.utm_content || '',
          utm_term: parsed.utm_term || '',
          clickid: parsed.clickid || clickidFromUrl || '',
        };
      } catch (_) {}
    }
    return {
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_campaign: urlParams.get('utm_campaign') || '',
      utm_content: urlParams.get('utm_content') || '',
      utm_term: urlParams.get('utm_term') || '',
      clickid: clickidFromUrl || '',
    };
  };

  const formatPAN = (pan) => {
    const cleaned = pan.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 10);

    if (cleaned.length <= 5) {
      return cleaned;
    } else if (cleaned.length <= 9) {
      return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    } else {
      return `${cleaned.slice(0, 5)} ${cleaned.slice(5, 9)} ${cleaned.slice(9)}`;
    }
  };

  const handlePANKeyDown = (e) => {
    if ([8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }

    const input = e.target;
    const currentValue = formData.pan.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    const cursorPos = input.selectionStart;
    const valueBeforeCursor = currentValue.slice(0, cursorPos);
    const position = valueBeforeCursor.length;

    let allowedPattern;
    if (position < 5) {
      allowedPattern = /^[A-Z]$/i;
    } else if (position < 9) {
      allowedPattern = /^[0-9]$/;
    } else if (position === 9) {
      allowedPattern = /^[A-Z]$/i;
    } else {
      e.preventDefault();
      return;
    }

    if (!allowedPattern.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let val = value;
    if (name === 'pan') {
      const cursorPos = e.target.selectionStart;
      const cleaned = value.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 10);

      let formatted = '';
      for (let i = 0; i < cleaned.length; i++) {
        const char = cleaned[i];
        if (i < 5) {
          if (/[A-Z]/.test(char)) {
            formatted += char;
          }
        } else if (i < 9) {
          if (/[0-9]/.test(char)) {
            formatted += char;
          }
        } else if (i === 9) {
          if (/[A-Z]/.test(char)) {
            formatted += char;
          }
        }
      }

      val = formatted;
      const displayValue = formatPAN(formatted);

      setFormData((prev) => ({ ...prev, [name]: val }));

      setTimeout(() => {
        e.target.value = displayValue;
        const spacesBeforeCursor = displayValue.slice(0, cursorPos).split(' ').length - 1;
        const newCursorPos = Math.min(cursorPos + spacesBeforeCursor, displayValue.length);
        e.target.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    } else if (name === 'phone') {
      val = value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: val }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: val }));
    }
    setErrors((prev) => ({ ...prev, [name]: '' }));
    setApiError('');
  };

  const handleContinueJourney = async () => {
    try {
      setIsContinuingJourney(true);
      const journey = await squidloansUserAPI.getJourney();
      const stage = journey?.journey?.lead_stage || nextEventName;
      if (stage && leadStageToRouteMap[stage]) {
        navigate(leadStageToRouteMap[stage]);
      } else {
        navigate('/apply-now');
      }
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 401) {
        console.error('Authentication error:', error);
        navigate('/apply-now');
      } else {
        if (nextEventName && leadStageToRouteMap[nextEventName]) {
          navigate(leadStageToRouteMap[nextEventName]);
        } else {
          navigate('/apply-now');
        }
      }
    } finally {
      setIsContinuingJourney(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({ pan: '', phone: '' });
    setShowConsentError(false);
    setApiError('');

    if (!formData.pan) {
      setErrors((prev) => ({ ...prev, pan: 'PAN is required' }));
      return;
    }
    if (!validatePAN(formData.pan)) {
      setErrors((prev) => ({ ...prev, pan: 'Invalid PAN format' }));
      return;
    }
    if (!formData.phone) {
      setErrors((prev) => ({ ...prev, phone: 'Mobile number is required' }));
      return;
    }
    if (!validatePhone(formData.phone)) {
      setErrors((prev) => ({ ...prev, phone: 'Invalid mobile number' }));
      return;
    }
    if (!agreed) {
      setShowConsentError(true);
      return;
    }
    if (!notPoliticallyExposed) {
      setApiError('Please confirm that you are not a politically exposed person');
      return;
    }

    setIsLoading(true);
    const utmParams = getUTMParameters();
    const PAN = formData.pan.trim().toUpperCase();
    const phone_number = formData.phone.trim();

    try {
      const response = await squidloansAuthAPI.sendOTP({ PAN, phone_number });

      if (response?.status === 200) {
        const stateData = { PAN, phone_number, ...utmParams };
        sessionStorage.setItem('panMobileData', JSON.stringify(stateData));
        navigate('/apply/otp-mobile', { state: stateData });
      } else {
        setApiError(response?.data?.message || 'Failed to send OTP. Please try again.');
      }
    } catch (error) {
      setApiError(error.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fieldLabelClass =
    'text-sm sm:text-base font-medium text-[#1E3A8A] mb-1.5 block';

  const inputWrapperClass = (hasError) =>
    `flex items-center gap-3 w-full rounded-xl border bg-white transition-all ${
      hasError
        ? 'border-red-400 ring-2 ring-red-100'
        : 'border-[#B8D4F0] focus-within:border-[#FF6B35] focus-within:ring-2 focus-within:ring-[#FF6B35]/20'
    }`;

  const inputClass =
    'flex-1 min-w-0 py-3.5 pr-4 bg-transparent text-sm sm:text-base text-[#1E3A8A] placeholder:text-[#94A3B8] focus:outline-none';

  return (
    <div className="min-h-screen bg-[#F8FAFC] md:bg-white text-[#1E293B] flex flex-col">
      <section
        className="pan-mobile-hero mt-20 sm:mt-0 lg:mt-20"
        style={{ '--pan-mobile-banner': `url(${landingBanner})` }}
      >
        <div className="pan-mobile-hero-inner sm:px-6 lg:px-10">
          <div className="pan-mobile-form-wrap">
            {!checkingAuth && isAuthenticated && verifiedPhone && (
              <div className="pan-mobile-form-card overflow-hidden rounded-2xl">
                <div className="h-1 bg-gradient-to-r from-[#22c55e] via-[#4ade80] to-[#22c55e]" />
                <div className="p-5 sm:p-6 flex flex-col items-center text-center space-y-3">
                  <div className="bg-green-500 p-2.5 rounded-full shadow-md shadow-green-200">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#1E3A8A] mb-1">
                      Your mobile number has been verified
                    </h3>
                    <p className="text-sm text-[#64748B]">
                      Continue your loan application journey
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleContinueJourney}
                    disabled={isContinuingJourney}
                    className="w-full py-4 rounded-xl bg-[#FF6B35] hover:bg-[#E55A2B] text-white text-sm font-bold uppercase tracking-wide transition-all disabled:opacity-70 shadow-lg shadow-[#FF6B35]/30"
                  >
                    {isContinuingJourney ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading...
                      </span>
                    ) : (
                      'Continue Journey'
                    )}
                  </button>
                </div>
              </div>
            )}

            {!checkingAuth && !isAuthenticated && (
              <div className="pan-mobile-form-card overflow-hidden rounded-2xl w-full">
                <div className="p-4 sm:p-7">
                  <p className="pan-mobile-form-title">
                    Enter Your Details
                  </p>
                  <p className="pan-mobile-form-subtitle">
                    Please provide your PAN and Mobile Number to continue
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                    <div>
                      <label htmlFor="pan" className={fieldLabelClass}>
                        PAN Number <span className="text-[#FF6B35]">*</span>
                      </label>
                      <div className={inputWrapperClass(errors.pan)}>
                        <span className="pl-4 text-[#1E3A8A]/50 flex-shrink-0">
                          <CreditCard className="h-5 w-5" strokeWidth={1.75} />
                        </span>
                        <input
                          type="text"
                          id="pan"
                          name="pan"
                          value={formatPAN(formData.pan)}
                          onChange={handleInputChange}
                          onKeyDown={handlePANKeyDown}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pastedText = (e.clipboardData || window.clipboardData).getData('text');
                            const cleaned = pastedText.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 10);
                            let formatted = '';
                            for (let i = 0; i < cleaned.length; i++) {
                              const char = cleaned[i];
                              if (i < 5 && /[A-Z]/.test(char)) {
                                formatted += char;
                              } else if (i >= 5 && i < 9 && /[0-9]/.test(char)) {
                                formatted += char;
                              } else if (i === 9 && /[A-Z]/.test(char)) {
                                formatted += char;
                              }
                            }
                            setFormData((prev) => ({ ...prev, pan: formatted }));
                            setErrors((prev) => ({ ...prev, pan: '' }));
                            setApiError('');
                          }}
                          placeholder="ABCDE 1234 F"
                          maxLength={12}
                          autoComplete="off"
                          className={`${inputClass} uppercase tracking-widest font-semibold`}
                        />
                      </div>
                      {errors.pan && <p className="text-xs text-red-500 mt-1.5 pl-1">{errors.pan}</p>}
                    </div>

                    <div>
                      <label htmlFor="phone" className={fieldLabelClass}>
                        Mobile Number <span className="text-[#FF6B35]">*</span>
                      </label>
                      <div className={inputWrapperClass(errors.phone)}>
                        <span className="pl-4 text-[#1E3A8A]/50 flex-shrink-0">
                          <Smartphone className="h-5 w-5" strokeWidth={1.75} />
                        </span>
                        <span className="text-sm font-semibold text-[#1E3A8A] border-r border-[#D1DCE8] pr-3 flex-shrink-0">
                          +91
                        </span>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="10 digit mobile number"
                          autoComplete="off"
                          maxLength={10}
                          className={inputClass}
                        />
                      </div>
                      {errors.phone && <p className="text-xs text-red-500 mt-1.5 pl-1">{errors.phone}</p>}
                    </div>

                    <div className="space-y-3 pt-1 border-t border-[#E8EEF4]">
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={agreed}
                          onChange={() => {
                            setAgreed((prev) => !prev);
                            setShowConsentError(false);
                          }}
                          className="mt-0.5 h-4 w-4 rounded border-[#B8D4F0] accent-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/30 cursor-pointer flex-shrink-0"
                        />
                        <span className="text-[11px] sm:text-xs text-[#475569] leading-relaxed group-hover:text-[#1E3A8A] transition-colors">
                          I agree to the{' '}
                          <Link
                            to="/terms-condition"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#FF6B35] font-semibold hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              window.open('/terms-condition', '_blank');
                            }}
                          >
                            Terms & Conditions
                          </Link>{' '}
                          and{' '}
                          <Link
                            to="/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#FF6B35] font-semibold hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              window.open('/privacy-policy', '_blank');
                            }}
                          >
                            Privacy Policy
                          </Link>
                        </span>
                      </label>
                      {showConsentError && (
                        <p className="text-xs text-red-500 pl-7">Please agree to the terms and conditions</p>
                      )}

                      <label className="flex items-start gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={notPoliticallyExposed}
                          onChange={() => setNotPoliticallyExposed((prev) => !prev)}
                          className="mt-0.5 h-4 w-4 rounded border-[#B8D4F0] accent-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/30 cursor-pointer flex-shrink-0"
                        />
                        <span className="text-[11px] sm:text-xs text-[#475569] leading-relaxed group-hover:text-[#1E3A8A] transition-colors">
                          I confirm that I am not a{' '}
                          <span className="text-[#FF6B35] font-semibold">Politically Exposed Person (PEP)</span> or
                          related to one.
                        </span>
                      </label>
                    </div>

                    {apiError && (
                      <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-xs text-red-700">
                        {apiError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-4 rounded-xl bg-[#FF6B35] hover:bg-[#E55A2B] text-white text-sm sm:text-base font-bold tracking-wide uppercase transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-[#FF6B35]/30 hover:shadow-xl hover:shadow-[#FF6B35]/35 hover:-translate-y-0.5 active:translate-y-0"
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center gap-2 normal-case font-semibold">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Sending OTP...
                        </span>
                      ) : (
                        'Continue'
                      )}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {!checkingAuth && !isAuthenticated && (
        <>
        <section className="bg-[#F8FAFC] py-8 sm:py-10 px-4 sm:px-6 lg:px-10">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 sm:p-6 md:p-7 w-full max-w-4xl mx-auto shadow-lg">
            <h2 className="text-lg sm:text-xl font-bold text-[#FF6B35] mb-3 sm:mb-4 text-center">Rates and Fees</h2>
            <div className="space-y-0">
              <div className="flex justify-between items-center py-2 sm:py-2.5 border-b border-[#FFE4D6]">
                <span className="text-sm sm:text-base text-[#1E293B]">Monthly Interest Rate</span>
                <span className="text-sm sm:text-base text-[#1E293B] font-medium">2.9166</span>
              </div>
              <div className="flex justify-between items-center py-2 sm:py-2.5 border-b border-[#FFE4D6]">
                <span className="text-sm sm:text-base text-[#1E293B]">Offered Annual Percentage Rate (APR)</span>
                <span className="text-sm sm:text-base text-[#1E293B] font-medium">15% - 35%</span>
              </div>
              <div className="flex justify-between items-center py-2 sm:py-2.5 border-b border-[#FFE4D6]">
                <span className="text-sm sm:text-base text-[#1E293B]">Tenure/Repayment Period</span>
                <span className="text-sm sm:text-base text-[#1E293B] font-medium">1 - 3 Years</span>
              </div>
              <div className="flex justify-between items-center py-2 sm:py-2.5 border-b border-[#FFE4D6]">
                <span className="text-sm sm:text-base text-[#1E293B]">Minimum-Maximum Loan Amount</span>
                <span className="text-sm sm:text-base text-[#1E293B] font-medium">INR 10,000 - 3,00,000</span>
              </div>
              <div className="flex justify-between items-center py-2 sm:py-2.5 border-b border-[#FFE4D6]">
                <span className="text-sm sm:text-base text-[#1E293B]">Processing Fee</span>
                <span className="text-sm sm:text-base text-[#1E293B] font-medium">2%</span>
              </div>
              <div className="flex justify-between items-center py-2 sm:py-2.5 border-b border-[#FFE4D6]">
                <span className="text-sm sm:text-base text-[#1E293B]">GST on Processing Fee (Exclusive)</span>
                <span className="text-sm sm:text-base text-[#1E293B] font-medium">18%</span>
              </div>
            </div>

            <h3 className="text-base sm:text-lg font-bold text-[#FF6B35] mt-5 sm:mt-6 mb-2 sm:mb-3 text-center">
              Representative Example:
            </h3>
            <div className="overflow-x-auto rounded-xl border border-[#E2E8F0]">
              <table className="w-full min-w-[640px] text-sm sm:text-base border-collapse">
                <thead>
                  <tr className="bg-[#FFF7ED] font-semibold text-[#1E293B] border-b-2 border-[#FF6B35]/30">
                    <th className="px-2 sm:px-3 py-2 sm:py-3 text-left">Loan Amount</th>
                    <th className="px-2 sm:px-3 py-2 sm:py-3 text-left">APR</th>
                    <th className="px-2 sm:px-3 py-2 sm:py-3 text-left">Tenure</th>
                    <th className="px-2 sm:px-3 py-2 sm:py-3 text-left">Processing Fee</th>
                    <th className="px-2 sm:px-3 py-2 sm:py-3 text-left">GST on Processing Fee</th>
                    <th className="px-2 sm:px-3 py-2 sm:py-3 text-left">Amount Disbursed</th>
                    <th className="px-2 sm:px-3 py-2 sm:py-3 text-left">EMI</th>
                    <th className="px-2 sm:px-3 py-2 sm:py-3 text-left">Total Repayment Amount</th>
                    <th className="px-2 sm:px-3 py-2 sm:py-3 text-left">Total Interest</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0] text-[#1E293B]">
                    <td className="px-2 sm:px-3 py-2 sm:py-3">₹50,000</td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3">35%</td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3">12 Months</td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3">₹1,000</td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3">₹180</td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3">₹48,820</td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3">₹4,998</td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3">₹59,978</td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3">₹9,978</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-base sm:text-lg font-bold text-[#FF6B35] mt-5 sm:mt-6 mb-2 sm:mb-3 text-center">
              Company Accreditation
            </h3>
            <p className="text-sm sm:text-base text-[#64748B] text-center leading-relaxed max-w-2xl mx-auto">
              Snap Paisa is a digital lending platform. NBFC Partner: Ampire Finance Pvt. Ltd., a registered
              Non-Banking Financial Company (NBFC) duly licensed and regulated by the Reserve Bank of India (RBI).
            </p>
          </div>
        </section>

        {/* Loan Calculator Section */}
        <section className="calculator-section">
          <div className="calculator-full-width">
            <motion.div
              className="calculator-wrapper"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Estimate Your Monthly Payment</h2>
              <p>Use our payment calculator to plan your loan repayment schedule</p>
              <InterestCalculator />
            </motion.div>
          </div>
        </section>

        {/* Modern Services Section */}
        <section className="modern-services-section">
          <div className="modern-services-container">
            <motion.div
              className="modern-services-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="modern-services-title">
                Experience <span className="highlight">Seamless</span> Loan Services
              </h2>
              <p className="modern-services-subtitle">
                Our advanced technology ensures quick processing and secure transactions.
                Get the financial support you need with our reliable lending solutions.
              </p>
            </motion.div>

            <div className="modern-services-grid">
              {[
                {
                  icon: '🚀',
                  title: 'Lightning Fast',
                  description: 'Get approved and receive funds within minutes, not days',
                },
                {
                  icon: '🛡️',
                  title: 'Bank-Level Security',
                  description: 'Your data is protected with enterprise-grade encryption',
                },
                {
                  icon: '📊',
                  title: 'Smart Analytics',
                  description: 'AI-powered risk assessment for instant decisions',
                },
                {
                  icon: '💳',
                  title: 'Flexible Repayment',
                  description: 'Choose payment schedules that work for your budget',
                },
              ].map((service, index) => (
                <motion.div
                  key={index}
                  className="modern-service-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="service-icon">{service.icon}</div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="why-choose-section">
          <div className="why-choose-container">
            <motion.h2
              className="why-choose-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              WHY CHOOSE SNAP PAISA?
            </motion.h2>
            <div className="why-choose-grid">
              {[
                {
                  title: 'Instant Approval',
                  description:
                    'Get loan approval within minutes with our AI-powered assessment system',
                },
                {
                  title: 'Bank-Level Security',
                  description:
                    'Enterprise-level security with complete data encryption for risk-free operations',
                },
                {
                  title: 'Advanced Technology',
                  description:
                    'Leveraging cutting-edge technologies to empower quick and secure lending',
                },
                {
                  title: 'Customer-Focused',
                  description:
                    'Enhancing borrower experience with efficient, secure, and streamlined operations',
                },
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  className="why-choose-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <h3 className="why-choose-card-title">{feature.title}</h3>
                  <p className="why-choose-card-description">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        </>
      )}

      <FlowFooter />
    </div>
  );
}

export default PanMobile;
